// Mirrors the C# UserMemoryResponse / note DTO (#2220). The AI does not yet read these notes into a
// turn — this is the user-facing control surface only (opt-in, read, delete); actual injection is a
// later step. Fields are camelCase to match the backend's JSON serialization.
export interface UserMemoryNote {
    id: string;
    description: string;
    content: string;
    createdAt: string;
    updatedAt: string;
}

export interface UserMemory {
    optedIn: boolean;
    notes: UserMemoryNote[];
}

// Mirrors the C# UpdateUserMemoryOptInRequest. The server only reads `optedIn` on
// PUT /memory/me/opt-in.
export interface UpdateUserMemoryOptInRequest {
    optedIn: boolean;
}

// Initial Redux value before GET /memory/me resolves. Opted-out with no notes mirrors the backend
// default for a user who has never chosen; the server's value overwrites it once fetched, and the
// whole surface is gated on UserMemoryEnabled anyway.
export const defaultUserMemory: UserMemory = {
    optedIn: false,
    notes: [],
};

const isUserMemoryNote = (value: unknown): value is UserMemoryNote => {
    if (typeof value !== 'object' || value === null) {
        return false;
    }
    const candidate = value as Partial<UserMemoryNote>;
    return (
        typeof candidate.id === 'string' &&
        typeof candidate.description === 'string' &&
        typeof candidate.content === 'string' &&
        typeof candidate.createdAt === 'string' &&
        typeof candidate.updatedAt === 'string'
    );
};

// BaseService.getResponseAsync returns a shapeless `{}` (not undefined) on a 429 or an empty-body
// 2xx, typed as whatever the caller asked for. Validate the shape at the service boundary so such a
// response is rejected rather than reaching the store as a note-less object — the settings section
// reads `memory.notes.length`, which throws a TypeError on a bare `{}`. Each note is validated too:
// `Array.isArray` alone would let a malformed element (e.g. `notes: [null]`) through and then crash
// the render at `note.id`, so a well-formed array of ill-formed notes is still rejected here.
export const isUserMemory = (value: unknown): value is UserMemory => {
    if (typeof value !== 'object' || value === null) {
        return false;
    }
    const candidate = value as Partial<UserMemory>;
    return (
        typeof candidate.optedIn === 'boolean' &&
        Array.isArray(candidate.notes) &&
        candidate.notes.every(isUserMemoryNote)
    );
};
